import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, X, CheckCircle2, Sparkles } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * One-time interactive walkthrough of the ambassador dashboard.
 * - Spotlight overlay with a 4-corner cutout that highlights each target element.
 * - 7 steps, each anchored to a `data-tutorial-target` selector.
 * - Skip is allowed but warns the user it'll come back next visit.
 * - Only marked complete when the user finishes ALL steps.
 */
const STEPS = [
  {
    target: 'tutorial-stats',
    title: 'Your at-a-glance stats',
    body: 'These four cards show your bottles sold this month, revenue, all-time bottles, and your wallet balance. They update automatically as orders come in.',
  },
  {
    target: 'tutorial-customer-link',
    title: 'Customer Referral Link',
    body: 'Share this link on TikTok, Facebook, or any chat. When customers buy through it, you earn the per-bottle profit. The cookie holds for 30 days, so even later returns count.',
  },
  {
    target: 'tutorial-recruit-link',
    title: 'Recruit Link — ₱200 each',
    body: 'When someone signs up as an ambassador via this link AND their tester kit is approved, you get a one-time ₱200 bonus credited to your wallet. Direct referrals only — no pyramiding.',
  },
  {
    target: 'tutorial-growth',
    title: 'Last 30 days of momentum',
    body: 'Your sales by day, with today highlighted in gold. Hover any bar to see exact numbers. Screenshot this for your TikTok flex posts.',
  },
  {
    target: 'tutorial-leaderboard',
    title: 'Team Leaderboard',
    body: 'See where you rank vs. other ambassadors this week, this month, or all-time. Top of the month gets a free bottle, cash bonus, certificate, and spotlight feature.',
  },
  {
    target: 'tutorial-wallet',
    title: 'Wallet activity',
    body: 'Every ₱200 referral bonus and any other credits show up here. Wallet credit can be applied to your next product order or cashed out per the schedule we share.',
  },
  {
    target: 'tutorial-cert',
    title: 'Top-Seller Certificate',
    body: "If you're the #1 seller this month, click here to print your official certificate. Post it everywhere — it's your proof of #1.",
  },
];

export default function DashboardTutorial({ alreadyCompleted, onComplete }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [showSkipWarn, setShowSkipWarn] = useState(false);

  // Auto-open if not completed
  useEffect(() => {
    if (alreadyCompleted) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [alreadyCompleted]);

  // Recompute spotlight rect when step changes / on resize
  const updateRect = useCallback(() => {
    if (!open) { setRect(null); return; }
    const target = STEPS[step]?.target;
    const el = target ? document.querySelector(`[data-tutorial-target="${target}"]`) : null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    // Scroll into view if the element is below the fold
    if (r.top < 80 || r.bottom > window.innerHeight - 200) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Re-measure shortly after scroll
      setTimeout(() => {
        const r2 = el.getBoundingClientRect();
        setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      }, 350);
    }
  }, [open, step]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [updateRect]);

  const total = STEPS.length;
  const cur = STEPS[step] || STEPS[0];
  const isLast = step === total - 1;

  const next = () => isLast ? finish() : setStep(s => Math.min(total - 1, s + 1));
  const prev = () => setStep(s => Math.max(0, s - 1));

  const finish = async () => {
    try {
      await axios.post(`${API}/ambassador/me/complete-tutorial`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      toast.success('Tutorial complete — you\'re ready to push.');
      setOpen(false);
      onComplete?.();
    } catch {
      toast.error('Could not save your progress. Try again.');
    }
  };

  const trySkip = () => setShowSkipWarn(true);
  const confirmSkip = () => {
    setShowSkipWarn(false);
    setOpen(false);
    toast('We\'ll show this tutorial again next time you log in.', { duration: 4000 });
  };

  if (!open) return null;

  // Tooltip placement: prefer below the target, fall back to above if too low
  const TOOLTIP_W = 360;
  const PAD = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let tipTop = rect ? rect.top + rect.height + PAD : vh / 2 - 100;
  let tipLeft = rect ? Math.min(Math.max(rect.left, 16), vw - TOOLTIP_W - 16) : vw / 2 - TOOLTIP_W / 2;
  // Flip above if it would overflow the viewport
  if (rect && tipTop + 240 > vh) tipTop = Math.max(16, rect.top - 240);

  return (
    <>
      {/* Spotlight overlay */}
      <div
        className="fixed inset-0 z-[90] pointer-events-auto"
        data-testid="dashboard-tutorial"
        style={rect ? {
          background: 'rgba(0,0,0,0.78)',
          WebkitMask: `linear-gradient(#000,#000) content-box, linear-gradient(#000,#000)`,
          maskComposite: 'exclude',
          clipPath: `polygon(
            0 0, 100% 0, 100% 100%, 0 100%, 0 0,
            ${rect.left - 6}px ${rect.top - 6}px,
            ${rect.left - 6}px ${rect.top + rect.height + 6}px,
            ${rect.left + rect.width + 6}px ${rect.top + rect.height + 6}px,
            ${rect.left + rect.width + 6}px ${rect.top - 6}px,
            ${rect.left - 6}px ${rect.top - 6}px
          )`,
        } : { background: 'rgba(0,0,0,0.78)' }}
      />

      {/* Highlighted ring around target */}
      {rect && (
        <div
          className="fixed z-[91] pointer-events-none border-2 border-[#D4AF37] shadow-[0_0_0_2px_rgba(212,175,55,0.25)]"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="fixed z-[92] bg-[#0F0F0F] border border-[#D4AF37]/40 shadow-2xl p-5 sm:p-6 animate-fade-in-up"
        style={{ top: tipTop, left: tipLeft, width: 'min(92vw,360px)' }}
        data-testid="tutorial-tooltip"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-sans flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Step {step + 1} of {total}
          </p>
          <button onClick={trySkip} className="text-white/40 hover:text-white p-1" aria-label="Skip tutorial" data-testid="tutorial-skip">
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="text-xl text-white font-light mb-2 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {cur.title}
        </h3>
        <p className="text-white/60 text-sm font-sans leading-relaxed mb-5">
          {cur.body}
        </p>

        {/* Progress bar */}
        <div className="flex gap-1 mb-5">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-0.5 transition-colors ${i <= step ? 'bg-[#D4AF37]' : 'bg-white/15'}`} />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={prev}
            disabled={step === 0}
            className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed font-sans"
            data-testid="tutorial-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button
            type="button"
            onClick={next}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black uppercase tracking-[0.22em] text-xs font-sans hover:bg-[#E5C158] transition-colors"
            data-testid="tutorial-next"
          >
            {isLast ? (<><CheckCircle2 className="w-3.5 h-3.5" /> Finish</>) : (<>Next <ChevronRight className="w-3.5 h-3.5" /></>)}
          </button>
        </div>
      </div>

      {/* Skip-warning modal */}
      {showSkipWarn && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setShowSkipWarn(false)} aria-hidden="true" />
          <div className="relative max-w-sm w-full bg-[#0F0F0F] border border-[#D4AF37]/40 p-6 text-center" data-testid="tutorial-skip-warning">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3 font-sans">Skip Tutorial?</p>
            <h3 className="text-xl text-white font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              You&apos;ll be back here next visit.
            </h3>
            <p className="text-white/60 text-sm font-sans mb-5 leading-relaxed">
              The tutorial keeps reappearing until you complete it. It&apos;s only 7 short steps — we promise it&apos;s worth it.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowSkipWarn(false)} className="flex-1 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#E5C158]" data-testid="tutorial-resume">
                Continue tutorial
              </button>
              <button onClick={confirmSkip} className="flex-1 py-3 border border-white/15 text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-sans" data-testid="tutorial-skip-confirm">
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
